import React from 'react';
import parse from 'html-react-parser';

interface HighlightTextProps {
    text: string;
    keyword: string;
    sub_keyword?: string;
    opacity_bg?: number;
    is_highlight_date?: boolean;
}

const regexSpecialChars = ['\\', '^', '$', '*', '+', '?', '.', '(', ')', '|', '{', '}', '[', ']', '-'];
const regexEscapePattern = new RegExp(`[${regexSpecialChars.map((char) => `\\${char}`).join('')}]`, 'g');

const escapeRegExp = (value: string) => value.replace(regexEscapePattern, '\\$&');

const datePatternParts = [
    '(?:19|20|21)\\d{2}\\s*년\\s*(?:1[0-2]|0?[1-9])\\s*월\\s*(?:3[01]|[12]\\d|0?[1-9])\\s*일',
    '(?:19|20|21)\\d{2}\\s*년\\s*(?:1[0-2]|0?[1-9])\\s*월',
    '(?:19|20|21)\\d{2}\\s*년',
    '(?:1[0-2]|0?[1-9])\\s*월\\s*(?:3[01]|[12]\\d|0?[1-9])\\s*일',
    '(?:지난|오는|다음|이번)\\s*(?:3[01]|[12]\\d|0?[1-9])\\s*일',
    '(?:지난|오는|다음|이번)\\s*(?:1[0-2]|0?[1-9])\\s*월',
    '(?:1[0-2]|0?[1-9])\\s*월',
    '(?:3[01]|[12]\\d|0?[1-9])\\s*일',
];

const datePattern = `(?:${datePatternParts.join('|')})`;

const HighlightText: React.FC<HighlightTextProps> = ({ text, keyword, sub_keyword, opacity_bg = 1, is_highlight_date = false }) => {
    const hasKeyword = Boolean(keyword && keyword.trim());
    const hasSubKeyword = Boolean(sub_keyword && sub_keyword.trim());

    if (!hasKeyword && !hasSubKeyword && !is_highlight_date) return <span>{text}</span>;

    const keywordRegex = hasKeyword ? new RegExp(escapeRegExp(keyword), 'gi') : null;
    const subKeywordRegex = hasSubKeyword ? new RegExp(escapeRegExp(sub_keyword as string), 'gi') : null;
    const dateRegex = is_highlight_date ? new RegExp(datePattern, 'gi') : null;

    const matchers = (
        [
            keywordRegex && {
                regex: keywordRegex,
                render: (match: string, key: number) => (
                    <span key={key} style={{ backgroundColor: `rgba(255, 255, 0, ${opacity_bg})` }}>
                        {match}
                    </span>
                ),
            },
            subKeywordRegex && {
                regex: subKeywordRegex,
                render: (match: string, key: number) => (
                    <span key={key} style={{ backgroundColor: `rgba(168, 210, 251, ${opacity_bg})` }}>
                        {match}
                    </span>
                ),
            },
            dateRegex && {
                regex: dateRegex,
                render: (match: string, key: number) => (
                    <span key={key} style={{ backgroundColor: `rgba(183, 234, 255, ${opacity_bg})` }}>
                        {match}
                    </span>
                ),
            },
        ].filter(Boolean) as { regex: RegExp; render: (match: string, key: number) => React.ReactNode }[]
    );

    const highlightContent = (content: string) => {
        if (!matchers.length) return content;

        const result: React.ReactNode[] = [];
        let cursor = 0;
        let key = 0;

        while (cursor < content.length) {
            let nextMatch: any = null;

            matchers.forEach((matcher, matcherIndex) => {
                matcher.regex.lastIndex = cursor;
                const execResult = matcher.regex.exec(content);
                if (!execResult) return;

                const start = execResult.index;
                const end = start + execResult[0].length;

                if (
                    !nextMatch ||
                    start < nextMatch?.start ||
                    (start === nextMatch.start && matcherIndex < nextMatch.matcherIndex)
                ) {
                    nextMatch = { start, end, matcherIndex, match: execResult[0] };
                }
            });

            if (!nextMatch) {
                result.push(content.slice(cursor));
                break;
            }

            if (nextMatch?.start > cursor) {
                result.push(content.slice(cursor, nextMatch.start));
            }

            result.push(matchers[nextMatch.matcherIndex].render(nextMatch.match, key));
            key += 1;
            cursor = nextMatch?.end;
        }

        return result;
    };

    const parsedText = parse(text, {
        replace: (domNode: any) => {
            if (domNode.type === 'text') {
                return <>{highlightContent(domNode.data)}</>;
            }
        },
    });

    return <span>{parsedText}</span>;
};

export default HighlightText;
